import * as React from "react";
import { useTranslation, withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Input, Switch, Button, Form, Space, Row, DatePicker } from "antd";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { ModalComponentProps, withModal } from "Components/General/Modal/ModalContext";
import { FormInstance } from "antd/lib/form";
import { observer } from "mobx-react";
import { workflowsStore } from "Pages/Admin/Stores/WorkflowsStore";
import { isEmpty } from "utils/isEmpty";
import { dayjs } from "Config/dayjs";
import { WithModal } from "Components/General/Modal/withModal";
import { FlowchartModal } from "Pages/Project/Workflow/Pages/ProcessStep/Component/FlowchartModal";
const FormItem = Form.Item;

const ActiveSwitcher = (props) => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  return (
    <Space>
      {t("Activate")}
      <Switch {...props} />
    </Space>
  );
};

interface WorkflowFormProps extends WithModal, WithTranslation, ModalComponentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEdit?: (obj: any) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCreate?: (obj: any) => Promise<void>;
  initialData?: API_AdminWorkflowDefinitionParameters;
}

export const WorkflowForm = withTranslation(I18N_NS.ADMIN)(
  withModal(
    observer(
      class extends React.Component<WorkflowFormProps> {
        private formRef = React.createRef<FormInstance>();

        handleSubmit = async (values) => {
          try {
            if (!this.props.initialData) {
              await this.props.onCreate(values);
            } else {
              await this.props.onEdit({ ...values });
            }
            this.props.setConfirmOnCancel(false);
            this.props.hideModal();
            await workflowsStore.fetch();
          } catch (error) {
            throw error;
          }
        };

        handleActiveChange = (value: boolean) => {
          if (value === true) {
            workflowsStore.activate(this.props.initialData.id);
          } else {
            workflowsStore.deactivate(this.props.initialData.id);
          }
        };

        handleShowMockup = async () => {
          this.props.showModal(FlowchartModal, {
            modalProps: {
              footer: null,
              maskClosable: true,
              width: "100%",
              title: " ",
              style: { minHeight: 550 },
              zIndex: 1013,
              className: "full-height-modal",
            },
            componentProps: {
              definitionXml: this.props.initialData.definitionXml,
            },
          });
        };

        render() {
          const { t } = this.props;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const initialValues: { [key in keyof API_AdminWorkflowDefinitionParameters]: any } = this.props.initialData
            ? {
                id: this.props.initialData.id,
                name: this.props.initialData.name,
                definitionXml: this.props.initialData.definitionXml,
                created: dayjs(this.props.initialData.created),
                updateFailed: false,
                updateRunning: false,
                active: this.props.initialData.active,
              }
            : {
                updateFailed: false,
                updateRunning: false,
              };
          return (
            <Form
              ref={this.formRef}
              initialValues={initialValues}
              layout="vertical"
              onFinish={this.handleSubmit}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onValuesChange={async (changedValues: any) => {
                if (Object.keys(changedValues).length > 0) {
                  if (Object.prototype.hasOwnProperty.call(changedValues, "active")) {
                    this.handleActiveChange(changedValues.active);
                  }
                  this.props.setConfirmOnCancel(true);
                }
              }}
            >
              {Object.prototype.hasOwnProperty.call(initialValues, "id") && (
                <Row justify="space-between" align="middle">
                  <Space size="middle">
                    <FormItem name="id" label={t("ID")}>
                      <Input disabled={true} />
                    </FormItem>
                    <FormItem name="created" label={t("Date")}>
                      <DatePicker
                        disabled={true}
                        format="DD-MM-YYYY HH:mm"
                        disabledDate={(current) => current && current < dayjs().endOf("day")}
                      />
                    </FormItem>
                  </Space>
                  <FormItem
                    className={isEmpty(this.props.initialData) && "is-hidden"}
                    name="active"
                    noStyle
                    valuePropName="checked"
                  >
                    <ActiveSwitcher />
                  </FormItem>
                </Row>
              )}

              <FormItem
                name="definitionXml"
                label={t("Workflow")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input.TextArea autoSize={{ minRows: 5, maxRows: 10 }} />
              </FormItem>
              {this.props.initialData?.id && (
                <Button onClick={this.handleShowMockup} className="mb-md">
                  {t("Show diagram")}
                </Button>
              )}
              <Row align="middle">
                <FormItem valuePropName="checked" name="updateRunning" className="mb-1">
                  <Switch />
                </FormItem>
                <span className="ml-sm">{t("Change all running workflows (step names should not be changed)")}</span>
              </Row>
              <Row align="middle">
                <FormItem valuePropName="checked" name="updateFailed" className="mb-1">
                  <Switch />
                </FormItem>
                <span className="ml-sm">{t("Change all failed workflows (State = ERROR)")}</span>
              </Row>

              <ButtonsRow justify="space-between">
                <Button onClick={this.props.hideModal}>{t("Cancel")}</Button>
                <Button type="primary" loading={workflowsStore.state === "LOADING"} htmlType="submit">
                  {t("Save")}
                </Button>
              </ButtonsRow>
            </Form>
          );
        }
      }
    )
  )
);
